import React from 'react';

interface StaffModalProps {
  staffItem: { name: string; cost: number; notes: string | null };
  currentCount: number;
  remainingGold: number;
  onClose: () => void;
  onUpdate: (newCount: number) => void;
  isReadOnly?: boolean;
}

export const StaffModal: React.FC<StaffModalProps> = ({
  staffItem,
  currentCount,
  remainingGold,
  onClose,
  onUpdate,
  isReadOnly = false,
}) => {
  const totalCost = currentCount * staffItem.cost;
  const canAffordMore = remainingGold >= staffItem.cost;

  const decrement = () => {
    if (currentCount > 0) onUpdate(currentCount - 1);
  };

  const increment = () => {
    if (canAffordMore) onUpdate(currentCount + 1);
  };

  return (
    <div
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: '#fff', borderRadius: '12px', padding: '28px',
          width: '90%', maxWidth: '380px', boxSizing: 'border-box',
          boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px' }}>
          <div>
            <h3 style={{ margin: '0 0 4px 0', fontSize: '18px', color: '#0f172a' }}>{staffItem.name}</h3>
            {staffItem.notes && (
              <p style={{ margin: 0, fontSize: '13px', color: '#64748b' }}>{staffItem.notes}</p>
            )}
          </div>
          <button
            onClick={onClose}
            style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer', color: '#94a3b8', padding: '0 4px' }}
          >
            ×
          </button>
        </div>

        {/* Premium Budget Summary Card */}
        <div style={{
          background: '#f8fafc',
          border: '1px solid #e2e8f0',
          borderRadius: '8px',
          padding: '14px',
          marginBottom: '20px',
          fontSize: '14px',
          color: '#334155',
          display: 'flex',
          flexDirection: 'column',
          gap: '8px',
          boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.02)'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ color: '#64748b', fontWeight: '500' }}>Cost Each:</span>
            <strong style={{ color: '#0f172a', fontSize: '15px' }}>{staffItem.cost.toLocaleString()} GP</strong>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ color: '#64748b', fontWeight: '500' }}>Total Cost:</span>
            <strong style={{ color: totalCost > 0 ? '#2563eb' : '#94a3b8', fontSize: '15px' }}>
              {totalCost.toLocaleString()} GP
            </strong>
          </div>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center', 
            borderTop: '1px dashed #cbd5e1', 
            paddingTop: '8px', 
            marginTop: '4px' 
          }}>
            <span style={{ color: '#64748b', fontWeight: '500' }}>Remaining Treasury:</span>
            <strong style={{ 
              color: remainingGold >= 0 ? '#16a34a' : '#dc2626',
              fontSize: '15px' 
            }}>
              {remainingGold.toLocaleString()} GP
            </strong>
          </div>
        </div>

        {/* Counter or Reference only view */}
        {!isReadOnly ? (
          <>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '20px', marginBottom: '24px' }}>
              <button
                onClick={decrement}
                disabled={currentCount === 0}
                style={{
                  width: '44px', height: '44px', borderRadius: '50%',
                  border: '2px solid #e2e8f0', background: currentCount === 0 ? '#f1f5f9' : '#fff',
                  fontSize: '22px', fontWeight: 'bold', cursor: currentCount === 0 ? 'not-allowed' : 'pointer',
                  color: currentCount === 0 ? '#cbd5e1' : '#0f172a',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  transition: 'all 0.15s',
                }}
              >
                −
              </button>

              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '36px', fontWeight: 'bold', lineHeight: 1, color: '#0f172a' }}>{currentCount}</div>
                <div style={{ fontSize: '12px', color: '#94a3b8', marginTop: '4px' }}>hired</div>
              </div>

              <button
                onClick={increment}
                disabled={!canAffordMore}
                title={!canAffordMore ? 'Not enough treasury' : ''}
                style={{
                  width: '44px', height: '44px', borderRadius: '50%',
                  border: '2px solid #e2e8f0', background: canAffordMore ? '#2563eb' : '#f1f5f9',
                  fontSize: '22px', fontWeight: 'bold', cursor: canAffordMore ? 'pointer' : 'not-allowed',
                  color: canAffordMore ? '#fff' : '#cbd5e1',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  transition: 'all 0.15s',
                }}
              >
                +
              </button>
            </div>
            {!canAffordMore && (
              <p style={{ textAlign: 'center', fontSize: '12px', color: '#dc2626', margin: '0 0 16px 0' }}>
                Insufficient treasury to hire another
              </p>
            )}
          </>
        ) : (
          <div style={{ textAlign: 'center', marginBottom: '24px', padding: '16px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px' }}>
            <div style={{ fontSize: '14px', fontWeight: 'bold', color: '#475569', marginBottom: '4px' }}>🔒 Reference Only</div>
            <div style={{ fontSize: '12px', color: '#64748b', lineHeight: '1.4' }}>
              Under 2025 League rules, inducements are purchased during pre-game match setup rather than team draft.
            </div>
          </div>
        )}

        <button
          onClick={onClose}
          style={{
            width: '100%', padding: '12px', background: '#0f172a',
            color: '#fff', border: 'none', borderRadius: '8px',
            fontSize: '15px', fontWeight: '600', cursor: 'pointer',
          }}
        >
          Done
        </button>
      </div>
    </div>
  );
};
